import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, Car, Languages, ShieldAlert, Send } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { OFFICE } from "../data/content";
import { api } from "../lib/api";

const TOPICS = ["General question", "Immigration services", "Documents & identity", "ITIN & tax", "Screening & testing", "New life services", "Citizenship", "Partnership / referral", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.topic || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you. Your message has been received — we will respond soon.");
      setForm({ name: "", email: "", phone: "", topic: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Talk with a real person"
        lead="Questions about your journey? Call, email, visit, or send us a message. We respond to every inquiry."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-surface border border-line rounded-2xl p-6 shadow-sm space-y-4" data-testid="office-info-card">
              <h2 className="text-xl font-serif font-semibold text-navy">Our office</h2>
              <p className="flex items-start gap-3 text-sm text-ink"><MapPin className="h-4 w-4 text-teal mt-0.5 flex-shrink-0" /> {OFFICE.address}<br />{OFFICE.city}</p>
              <p className="flex items-center gap-3 text-sm text-ink"><Phone className="h-4 w-4 text-teal flex-shrink-0" /> <a href={`tel:${OFFICE.phone}`} className="hover:text-teal transition-colors">{OFFICE.phone}</a></p>
              <p className="flex items-center gap-3 text-sm text-ink"><Mail className="h-4 w-4 text-teal flex-shrink-0" /> <a href={`mailto:${OFFICE.email}`} className="hover:text-teal transition-colors">{OFFICE.email}</a></p>
            </div>

            <div className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="office-hours-card">
              <h3 className="text-base font-sans font-semibold text-navy flex items-center gap-2 mb-4"><Clock className="h-4 w-4 text-teal" /> Hours</h3>
              <div className="space-y-2">
                {OFFICE.hours.map((h) => (
                  <div key={h.days} className="flex justify-between text-sm">
                    <span className="text-mutedink">{h.days}</span>
                    <span className="text-ink font-medium">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-surface border border-line rounded-2xl p-6 shadow-sm space-y-3" data-testid="parking-card">
              <h3 className="text-base font-sans font-semibold text-navy flex items-center gap-2"><Car className="h-4 w-4 text-teal" /> Parking &amp; arrival</h3>
              <p className="text-sm text-mutedink leading-relaxed">Visitor parking is available in the building garage (entrance on NE 3rd Ave). Bring your ticket to reception for validation. The office is also steps from Broward County Transit routes and the Brightline Fort Lauderdale station.</p>
            </div>

            <div className="bg-sage-light border border-sage/30 rounded-2xl p-6" data-testid="language-assistance-card">
              <h3 className="text-base font-sans font-semibold text-navy flex items-center gap-2 mb-2"><Languages className="h-4 w-4 text-sage" /> Language assistance</h3>
              <p className="text-sm text-ink/80 leading-relaxed">English not your first language? Tell us your preferred language when you contact us and we will do our best to accommodate you, including interpreter coordination where available.</p>
            </div>

            <div className="rounded-2xl overflow-hidden border border-line shadow-sm" data-testid="office-map">
              <iframe
                title="CF Full Circle office map"
                src="https://www.google.com/maps?q=101+NE+3rd+Ave+Suite+1500+Fort+Lauderdale+FL+33301&output=embed"
                className="w-full h-64"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-surface border border-line rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">Send us a message</h2>
              <div className="mb-6 bg-champagne-light border border-champagne/50 rounded-xl p-4 flex gap-3" data-testid="confidential-warning">
                <ShieldAlert className="h-5 w-5 text-champagne-dark flex-shrink-0 mt-0.5" />
                <p className="text-sm text-ink/80 leading-relaxed">
                  <strong>Do not send confidential immigration documents through this form.</strong> Existing clients should upload confidential documents through the <Link to="/portal" className="text-teal font-medium hover:underline">secure Client Portal</Link>.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-name">Full name *</Label>
                    <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" required />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email *</Label>
                    <Input id="contact-email" type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-phone">Phone (optional)</Label>
                    <Input id="contact-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Topic *</Label>
                    <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="contact-topic-select"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                      <SelectContent>
                        {TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea id="contact-message" rows={5} data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" required />
                </div>
                <Button type="submit" disabled={sending} className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-8" data-testid="contact-submit-button">
                  {sending ? "Sending..." : <>Send Message <Send className="ml-2 h-4 w-4" /></>}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
